/**
 * ConfigSync Component
 * Syncs ConversationContext state with KV backend when authenticated
 */

import { useEffect, useRef, useCallback } from 'react';
import { useConversation } from '../contexts/ConversationContext';
import { useUserConfig, type UserConfig } from '../hooks/useUserConfig';

export function ConfigSync() {
  const { queryHistory, dateRange, setDateRange } = useConversation();
  const prevQueryHistoryRef = useRef(queryHistory);
  const prevDateRangeRef = useRef(dateRange);
  const initialSyncDoneRef = useRef(false);

  // Handle initial config load from KV
  const handleConfigLoaded = useCallback((config: UserConfig) => {
    if (initialSyncDoneRef.current) return;
    initialSyncDoneRef.current = true;

    // Merge KV config with local state (KV takes precedence for history)
    if (config.dateRange) {
      setDateRange({
        preset: config.dateRange.preset as 'today' | '7d' | '30d' | '90d' | 'all' | 'custom',
        startDate: config.dateRange.startDate,
        endDate: config.dateRange.endDate,
      });
    }

    // Note: queryHistory merge is more complex - we might want to merge rather than replace
    // For now, KV data takes precedence if it exists
    console.log('[ConfigSync] Loaded config from KV:', config);
  }, [setDateRange]);

  const { saveConfig } = useUserConfig({
    onConfigLoaded: handleConfigLoaded,
  });

  // Sync changes to KV when queryHistory changes
  useEffect(() => {
    if (!initialSyncDoneRef.current) return;

    if (prevQueryHistoryRef.current !== queryHistory) {
      prevQueryHistoryRef.current = queryHistory;
      saveConfig({ queryHistory });
    }
  }, [queryHistory, saveConfig]);

  // Sync changes to KV when dateRange changes
  useEffect(() => {
    if (!initialSyncDoneRef.current) return;

    if (prevDateRangeRef.current !== dateRange) {
      prevDateRangeRef.current = dateRange;
      saveConfig({ dateRange });
    }
  }, [dateRange, saveConfig]);

  // This component doesn't render anything
  return null;
}
